import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { toast } from 'sonner';
import { Home, Mail, Lock, User, Phone, Eye, EyeOff, Building2 } from 'lucide-react';

export const RegisterPage = () => {
  const { t, language } = useLanguage();
  const { register } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: '',
    user_type: 'particulier',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirm_password) {
      toast.error(language === 'fr' ? 'Les mots de passe ne correspondent pas' : 'كلمات المرور غير متطابقة');
      return;
    }

    if (formData.password.length < 6) {
      toast.error(language === 'fr' ? 'Le mot de passe doit contenir au moins 6 caractères' : 'يجب أن تحتوي كلمة المرور على 6 أحرف على الأقل');
      return;
    }

    setLoading(true);
    
    try {
      await register({
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        user_type: formData.user_type,
      });
      toast.success(language === 'fr' ? 'Inscription réussie !' : 'تم التسجيل بنجاح!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur lors de l\'inscription' : 'خطأ أثناء التسجيل'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="register-page">
      {/* Left side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1758523671819-06a0f1941520?w=1200"
          alt="Real Estate"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-blue-900/60" />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center text-white">
            <h2 className="font-manrope text-3xl font-bold mb-4">
              {language === 'fr' ? 'Rejoignez Beytam' : 'انضم إلى بيتم'}
            </h2>
            <p className="text-white/80 text-lg">
              {language === 'fr' ? 'Créez votre compte et commencez à chercher' : 'أنشئ حسابك وابدأ البحث'}
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 bg-blue-900 rounded-lg flex items-center justify-center">
              <Home className="w-6 h-6 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="font-manrope font-bold text-xl text-blue-900 leading-none">Beytam</span>
              <span className="text-xs text-slate-400 font-arabic">بيتم</span>
            </div>
          </Link>

          <div className="mb-8">
            <h1 className="font-manrope text-2xl font-bold text-slate-900 mb-2">{t('auth_register')}</h1>
            <p className="text-slate-600">
              {language === 'fr' ? 'Créez votre compte Beytam' : 'أنشئ حسابك في بيتم'}
            </p>
          </div>

          <Card className="border-0 shadow-lg">
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">{t('auth_fullname')}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="full_name"
                      name="full_name"
                      placeholder={language === 'fr' ? 'Votre nom' : 'اسمك'}
                      className="pl-10 h-11 rounded-lg"
                      value={formData.full_name}
                      onChange={handleChange}
                      required
                      data-testid="register-name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t('auth_email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="exemple@email.com"
                      className="pl-10 h-11 rounded-lg"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="register-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">{t('auth_phone')}</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="+213 XX XX XX XX"
                      className="pl-10 h-11 rounded-lg"
                      value={formData.phone}
                      onChange={handleChange}
                      data-testid="register-phone"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('auth_user_type')}</Label>
                  <RadioGroup 
                    value={formData.user_type} 
                    onValueChange={(value) => setFormData({ ...formData, user_type: value })}
                    className="flex gap-4"
                  >
                    <div className="flex-1">
                      <label 
                        htmlFor="particulier" 
                        className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                          formData.user_type === 'particulier' ? 'border-blue-900 bg-blue-50' : 'border-slate-200'
                        }`}
                      >
                        <RadioGroupItem value="particulier" id="particulier" className="sr-only" />
                        <User className="w-4 h-4" />
                        <span className="text-sm font-medium">{t('auth_particulier')}</span>
                      </label>
                    </div>
                    <div className="flex-1">
                      <label 
                        htmlFor="agence" 
                        className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                          formData.user_type === 'agence' ? 'border-blue-900 bg-blue-50' : 'border-slate-200'
                        }`}
                      >
                        <RadioGroupItem value="agence" id="agence" className="sr-only" />
                        <Building2 className="w-4 h-4" />
                        <span className="text-sm font-medium">{t('auth_agence')}</span>
                      </label>
                    </div>
                  </RadioGroup>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">{t('auth_password')}</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••"
                        className="pl-10 pr-10 h-11 rounded-lg"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        minLength={6}
                        data-testid="register-password"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        onClick={() => setShowPassword(!showPassword)}
                        data-testid="toggle-password-visibility"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm_password">{t('auth_confirm_password')}</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="confirm_password"
                        name="confirm_password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••"
                        className="pl-10 h-11 rounded-lg"
                        value={formData.confirm_password}
                        onChange={handleChange}
                        required
                        minLength={6}
                        data-testid="register-confirm-password"
                      />
                    </div>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 bg-rose-600 hover:bg-rose-700 rounded-lg" 
                  disabled={loading}
                  data-testid="register-submit"
                >
                  {loading ? t('common_loading') : t('auth_create_account')}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="mt-6 text-center text-sm text-slate-600">
            {t('auth_has_account')}{' '}
            <Link to="/login" className="text-blue-900 font-semibold hover:underline">
              {t('auth_login')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};
